import axios, { AxiosError, AxiosInstance } from 'axios'

declare module 'axios' {
  interface AxiosRequestConfig {
    skipErrorToast?: boolean
  }
}

import { useGlobalStore } from '@/stores/global-store'
import { Ory } from '@/plugins/ory'
import globalErrorHandler from './services/globalErrorHandler'
import { CustomerPermissionsStore } from './stores/customerPermissionsStore'
import { getWebsiteHost } from './helpers/returnUrlHelper'
import mockingService from './services/mockingService'

const ismockingActivated: boolean = import.meta.env.VITE_API_MOCKING_ACTIVATE == 'true'
const bssApiUrl = import.meta.env.VITE_APP_API
const bssApiKey = import.meta.env.VITE_APP_API_KEY

const iamApiUrl = import.meta.env.VITE_IAM_API_URL
const iamApiKey = import.meta.env.VITE_IAM_API_KEY
const orderApiUrl = import.meta.env.VITE_APP_ORDER_URL

const ORY_BASE_PATH = import.meta.env.VITE_APP_ORY_URL

const returnToUrl = getWebsiteHost()

const apiClients: Record<string, AxiosInstance> = {
  bss: createApiClient(bssApiUrl, bssApiKey),
  iam: createApiClient(iamApiUrl, iamApiKey),
  order: createApiClient(orderApiUrl, ''),
}

function createApiClient(baseUrl: string, apiKey: string) {
  return axios.create({
    baseURL: baseUrl,
    headers: {
      Accept: 'application/json',
      'X-Citymesh-API-Key': apiKey,
      'X-Gravitee-Cache': 'BY_PASS',
      'x-Relation-Contact-Id': null, // needs to be set
    },
    withCredentials: true,
  })
}

//interceptors
Object.entries(apiClients).forEach(([, apiClient]) => {
  apiClient.interceptors.request.use((request) => {
    const globalStore = useGlobalStore()
    const customerPermissionsStore = CustomerPermissionsStore()
    request.headers['Auth-session-id'] = globalStore?.orySession?.id
    request.headers['app_id'] = 'MY_CM_PORTAL'

    // add relationContactId to header
    if (customerPermissionsStore.selectedRelationContactId) {
      request.headers.set('X-Relation-Contact-Id', customerPermissionsStore.selectedRelationContactId)
      if (import.meta.env.DEV) {
        request.headers.set('RelationContactId', customerPermissionsStore.selectedRelationContactId)
      }
    }
    //add customerId queryParam (MIND REQUIREMENT)
    if (customerPermissionsStore.selectedCustomerId) {
      request.params = {
        ...request.params,
        customerId: customerPermissionsStore.selectedCustomerId,
      }
    }
    if (ismockingActivated) {
      mockingService.mockRequest(request)
    }

    return request
  })

  apiClient.interceptors.response.use(
    (response) => {
      if (ismockingActivated) {
        const mockedResponse = mockingService.mockResponse(response)
        if (mockedResponse) {
          return {
            ...response,
            ...mockedResponse,
          }
        }
      }
      return response
    },
    async (error: AxiosError) => {
      if (ismockingActivated && error.response) {
        const mockedResponse = mockingService.mockResponse(error.response)
        if (mockedResponse) {
          return Promise.resolve({
            ...mockedResponse,
          })
        }
      }
      if (!error.response) {
        const { eventBus } = await import('./services/eventBusService') // Dynamic import to avoid circular deps
        if (!navigator.onLine) {
          eventBus.emit('networkError', 'offline')
        } else {
          eventBus.emit('networkError', 'server-unreachable')
        }
        // Reject without calling globalErrorHandler to stop the log spam
        return Promise.reject(error)
      }
      if (error.response?.status === 401) {
        // double check with ORY to avoid API errors
        Ory.toSession().catch(() => (location.href = ORY_BASE_PATH + '/login?return_to=' + returnToUrl))
        return Promise.reject({ ...error.response, data: null })
      }

      if (error.response?.status === 403) {
        // forbidden -> should show error
        return Promise.reject({ ...error.response, data: null })
      }
      if (error.config?.skipErrorToast) {
        return Promise.reject(error)
      }
      globalErrorHandler.handleError(error)
      return Promise.reject(error)
    },
  )
})

export default apiClients
